/**
 * Family Collaboration Dashboard Component
 * Comprehensive admin dashboard for family storytelling collaboration
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Paper,
  Divider,
  IconButton,
  Chip,
  LinearProgress,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  useTheme,
  alpha,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Badge,
} from '@mui/material';
import {
  FamilyRestroom as FamilyIcon,
  People as PeopleIcon,
  Share as ShareIcon,
  Email as EmailIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  Assessment as AssessmentIcon,
  Schedule as ScheduleIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  Group as GroupIcon,
  BookmarkBorder as ContentIcon,
  Flag as FlagIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useSelector, useDispatch } from 'react-redux';
import TimeRangeFilter from '../common/TimeRangeFilter';
import useTimeRangeParam from '../common/useTimeRangeParam';
import { TimeRangeValue, resolveRangeYmd, toApiToken } from '../common/timeRange';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);
import { RootState, AppDispatch } from '../../store';
import { addNotification } from '../../store/uiSlice';
import { format, subDays, formatDistanceToNow } from 'date-fns';
import apiService from '../../services/api';
import MetricCard from '../common/MetricCard';
import { colors, spacing } from '../../theme/designTokens';

interface FamilyOverview {
  total_families: number;
  active_families: number;
  total_members: number;
  active_members: number;
  pending_invitations: number;
  shared_content: number;
  recent_activities: number;
  engagement_score: number;
  growth_metrics: {
    families_growth: number;
    members_growth: number;
    content_growth: number;
  };
}

interface FamilyEngagementData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    borderColor: string;
    backgroundColor: string;
    borderWidth: number;
    fill: boolean;
  }[];
}

interface TopFamily {
  id: string;
  family_name: string;
  members_count: number;
  stories_count: number;
  activity_score: number;
  created_at: string;
  last_activity: string;
}

interface ModerationOverview {
  flagged_content: number;
  pending_review: number;
  auto_approved: number;
  recent_flags: RecentFlag[];
}

interface RecentFlag {
  id: string;
  content_type: string;
  content_title: string;
  flagged_by: string;
  reason: string;
  severity: 'low' | 'medium' | 'high';
  created_at: string;
}

interface FamilyActivity {
  id: string;
  family_name: string;
  activity_type: string;
  actor_name: string;
  description: string;
  created_at: string;
  severity?: 'low' | 'medium' | 'high';
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`family-tabpanel-${index}`}
      aria-labelledby={`family-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export const FamilyCollaborationDashboard: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);

  const [tabValue, setTabValue] = useState(0);
  const [overview, setOverview] = useState<FamilyOverview | null>(null);
  const [engagementData, setEngagementData] = useState<FamilyEngagementData | null>(null);
  const [topFamilies, setTopFamilies] = useState<TopFamily[]>([]);
  const [moderationOverview, setModerationOverview] = useState<ModerationOverview | null>(null);
  const [recentActivities, setRecentActivities] = useState<FamilyActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [chartPeriod, setChartPeriod] = useState<'weekly' | 'monthly'>('weekly');
  const [digestAnalytics, setDigestAnalytics] = useState<any>(null);
  // `chartPeriod` above is a BUCKET size (weekly/monthly columns), not a range — it stays.
  const [digestTimeframe, setDigestTimeframe] = useTimeRangeParam({ type: '30d' }, { prefix: 'digest' });
  const [digestLoading, setDigestLoading] = useState(false);

  const loadFamilyDashboardData = async () => {
    try {
      console.log('🔄 Family Collaboration: Loading dashboard data sequentially to prevent auth race conditions...');

      // Load data sequentially instead of concurrently to prevent auth race conditions
      const responses = [];
      
      try {
        console.log('🔄 Family Collaboration: Loading overview...');
        const overviewResponse = await apiService.getFamilyOverview();
        responses.push({ status: 'fulfilled', value: overviewResponse });
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (error) {
        console.error('🚫 Family Collaboration: Error loading overview:', error);
        if (error && typeof error === 'object' && 'message' in error && error.message === 'Unauthorized') {
          console.log('🔐 Family Collaboration: Auth error detected, but not redirecting to prevent race condition');
          responses.push({ status: 'rejected', reason: error });
        } else {
          responses.push({ status: 'rejected', reason: error });
        }
      }

      try {
        console.log('🔄 Family Collaboration: Loading engagement chart...');
        const engagementResponse = await apiService.getFamilyEngagementChart(chartPeriod);
        responses.push({ status: 'fulfilled', value: engagementResponse });
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (error) {
        console.error('🚫 Family Collaboration: Error loading engagement:', error);
        if (error && typeof error === 'object' && 'message' in error && error.message === 'Unauthorized') {
          console.log('🔐 Family Collaboration: Auth error detected, but not redirecting to prevent race condition');
          responses.push({ status: 'rejected', reason: error });
        } else {
          responses.push({ status: 'rejected', reason: error });
        }
      }

      try {
        console.log('🔄 Family Collaboration: Loading top families...');
        const topFamiliesResponse = await apiService.getTopFamilies();
        responses.push({ status: 'fulfilled', value: topFamiliesResponse });
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (error) {
        console.error('🚫 Family Collaboration: Error loading top families:', error);
        if (error && typeof error === 'object' && 'message' in error && error.message === 'Unauthorized') {
          console.log('🔐 Family Collaboration: Auth error detected, but not redirecting to prevent race condition');
          responses.push({ status: 'rejected', reason: error });
        } else {
          responses.push({ status: 'rejected', reason: error });
        }
      }

      try {
        console.log('🔄 Family Collaboration: Loading moderation overview...');
        const moderationResponse = await apiService.getFamilyModerationOverview();
        responses.push({ status: 'fulfilled', value: moderationResponse });
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (error) {
        console.error('🚫 Family Collaboration: Error loading moderation:', error);
        if (error && typeof error === 'object' && 'message' in error && error.message === 'Unauthorized') {
          console.log('🔐 Family Collaboration: Auth error detected, but not redirecting to prevent race condition');
          responses.push({ status: 'rejected', reason: error });
        } else {
          responses.push({ status: 'rejected', reason: error });
        }
      }

      try {
        console.log('🔄 Family Collaboration: Loading recent activities...');
        const activitiesResponse = await apiService.getFamilyRecentActivities();
        responses.push({ status: 'fulfilled', value: activitiesResponse });
      } catch (error) {
        console.error('🚫 Family Collaboration: Error loading activities:', error);
        if (error && typeof error === 'object' && 'message' in error && error.message === 'Unauthorized') {
          console.log('🔐 Family Collaboration: Auth error detected, but not redirecting to prevent race condition');
          responses.push({ status: 'rejected', reason: error });
        } else {
          responses.push({ status: 'rejected', reason: error });
        }
      }

      const [
        overviewResponse,
        engagementResponse,
        topFamiliesResponse,
        moderationResponse,
        activitiesResponse
      ] = responses;

      let loadedSections = 0;
      const totalSections = 5;

      // Handle family overview
      if (overviewResponse.status === 'fulfilled' && overviewResponse.value?.data) {
        setOverview(overviewResponse.value.data);
        loadedSections++;
        console.log('✅ Family overview loaded successfully');
      } else {
        console.warn('⚠️ Family overview failed:', overviewResponse.status === 'rejected' ? overviewResponse.reason : 'No data');
        setOverview({
          total_families: 0,
          active_families: 0,
          total_members: 0,
          active_members: 0,
          pending_invitations: 0,
          shared_content: 0,
          recent_activities: 0,
          engagement_score: 0,
          growth_metrics: {
            families_growth: 0,
            members_growth: 0,
            content_growth: 0
          }
        });
      }

      // Handle engagement chart data
      if (engagementResponse.status === 'fulfilled' && engagementResponse.value?.data) {
        setEngagementData(engagementResponse.value.data);
        loadedSections++;
        console.log('✅ Family engagement chart loaded successfully');
      } else {
        console.warn('⚠️ Family engagement chart failed:', engagementResponse.status === 'rejected' ? engagementResponse.reason : 'No data');
        // Fallback chart data
        const fallbackLabels = chartPeriod === 'weekly' 
          ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
          : ['Week 1', 'Week 2', 'Week 3', 'Week 4'];
        
        setEngagementData({
          labels: fallbackLabels,
          datasets: [{
            label: 'Family Activities',
            data: fallbackLabels.map(() => Math.floor(Math.random() * 25) + 5),
            borderColor: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            borderWidth: 3,
            fill: true,
          }]
        });
      }

      // Handle top families
      if (topFamiliesResponse.status === 'fulfilled' && topFamiliesResponse.value?.data) {
        setTopFamilies(topFamiliesResponse.value.data.families || []);
        loadedSections++;
        console.log('✅ Top families loaded successfully');
      } else {
        console.warn('⚠️ Top families failed:', topFamiliesResponse.status === 'rejected' ? topFamiliesResponse.reason : 'No data');
        setTopFamilies([]);
      }

      // Handle moderation overview
      if (moderationResponse.status === 'fulfilled' && moderationResponse.value?.data) {
        setModerationOverview(moderationResponse.value.data);
        loadedSections++;
        console.log('✅ Moderation overview loaded successfully');
      } else {
        console.warn('⚠️ Moderation overview failed:', moderationResponse.status === 'rejected' ? moderationResponse.reason : 'No data');
        setModerationOverview({
          flagged_content: 0,
          pending_review: 0,
          auto_approved: 0,
          recent_flags: []
        });
      }

      // Handle recent activities
      if (activitiesResponse.status === 'fulfilled' && activitiesResponse.value?.data) {
        setRecentActivities(activitiesResponse.value.data.activities || []);
        loadedSections++;
        console.log('✅ Recent activities loaded successfully');
      } else {
        console.warn('⚠️ Recent activities failed:', activitiesResponse.status === 'rejected' ? activitiesResponse.reason : 'No data');
        setRecentActivities([
          {
            id: '1',
            family_name: 'Demo Family',
            activity_type: 'member_joined',
            actor_name: user?.name || 'Admin User',
            description: 'Family collaboration dashboard accessed',
            created_at: new Date().toISOString()
          }
        ]);
      }

      if (loadedSections > 0) {
        console.log(`✅ Family dashboard loaded: ${loadedSections}/${totalSections} sections successful`);
        if (loadedSections < totalSections) {
          dispatch(addNotification({
            type: 'warning',
            message: `Family dashboard loaded with ${totalSections - loadedSections} section(s) using fallback data`,
          }));
        }
      } else {
        dispatch(addNotification({
          type: 'error',
          message: 'Family dashboard loaded with fallback data only',
        }));
      }

    } catch (error) {
      console.error('❌ Family dashboard data loading error:', error);
      dispatch(addNotification({
        type: 'error',
        message: 'Failed to load family dashboard data - using fallback information',
      }));

      // Set all fallback data
      setOverview({
        total_families: 0,
        active_families: 0,
        total_members: 0,
        active_members: 0,
        pending_invitations: 0,
        shared_content: 0,
        recent_activities: 0,
        engagement_score: 0,
        growth_metrics: { families_growth: 0, members_growth: 0, content_growth: 0 }
      });
      setTopFamilies([]);
      setModerationOverview({
        flagged_content: 0,
        pending_review: 0,
        auto_approved: 0,
        recent_flags: []
      });
      setRecentActivities([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadFamilyDashboardData();
    setRefreshing(false);
    dispatch(addNotification({
      type: 'success',
      message: 'Family dashboard data refreshed',
    }));
  };

  const handleChartPeriodChange = (period: 'weekly' | 'monthly') => {
    setChartPeriod(period);
    // Reload engagement data with new period
    apiService.getFamilyEngagementChart(period)
      .then(response => {
        if (response?.data) {
          setEngagementData(response.data);
        }
      })
      .catch(error => {
        console.warn('Failed to update chart period:', error);
      });
  };

  const loadDigestAnalytics = async (value: TimeRangeValue) => {
    try {
      setDigestLoading(true);
      const timeframe = toApiToken(value);
      const custom = value.type === 'custom' ? resolveRangeYmd(value) : undefined;
      console.log('🔄 Loading digest analytics:', timeframe);
      const response = await apiService.getDigestAnalytics(timeframe, custom?.from, custom?.to);
      if (response?.data) {
        setDigestAnalytics(response.data);
        console.log('✅ Digest analytics loaded:', response.data);
      }
    } catch (error) {
      console.error('❌ Failed to load digest analytics:', error);
      dispatch(addNotification({
        type: 'error',
        message: 'Failed to load digest analytics',
      }));
    } finally {
      setDigestLoading(false);
    }
  };

  const handleDigestTimeframeChange = (value: TimeRangeValue) => {
    setDigestTimeframe(value);
    loadDigestAnalytics(value);
  };

  useEffect(() => {
    // Load data directly since ProtectedRoute already handles authentication
    console.log('🔄 Family Collaboration: Loading data (auth already verified by ProtectedRoute)');
    loadFamilyDashboardData();
    // Load digest analytics when Analytics tab is loaded
    loadDigestAnalytics(digestTimeframe);
  }, []);

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'member_joined':
      case 'member_invited':
        return <PeopleIcon />;
      case 'content_shared':
        return <ShareIcon />;
      case 'comment_added':
        return <NotificationsIcon />;
      case 'memory_added':
        return <ContentIcon />;
      default:
        return <FamilyIcon />;
    }
  };

  const getActivityColor = (type: string, severity?: string) => {
    if (severity === 'high') return theme.palette.error.main;
    if (severity === 'medium') return theme.palette.warning.main;
    
    switch (type) {
      case 'member_joined':
      case 'member_invited':
        return theme.palette.primary.main;
      case 'content_shared':
        return theme.palette.secondary.main;
      case 'comment_added':
        return theme.palette.info.main;
      case 'memory_added':
        return theme.palette.success.main;
      default:
        return theme.palette.grey[600];
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Skeleton variant="text" width="400px" height={40} />
          <Skeleton variant="text" width="500px" height={24} />
        </Box>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={140} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ p: spacing[6], bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: spacing[8] }}>
        <Box>
          <Typography
            variant="h1"
            sx={{
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: spacing[3],
              mb: spacing[2]
            }}
          >
            <FamilyIcon sx={{ fontSize: 40, color: colors.users.main }} />
            Family Collaboration
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.125rem' }}>
            Monitor family storytelling activities and engagement across the platform
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={refreshing}
        >
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={3} sx={{ mb: spacing[6] }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Active Families"
            value={overview?.active_families || 0}
            subtitle={`${overview?.total_families || 0} total families`}
            icon="family_restroom"
            color="users"
            change={overview?.growth_metrics?.families_growth || 0}
            trend={overview?.growth_metrics?.families_growth && overview.growth_metrics.families_growth > 0 ? 'up' : 'down'}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Family Members"
            value={overview?.active_members || 0}
            subtitle={`${overview?.total_members || 0} total members`}
            icon="people"
            color="users"
            change={overview?.growth_metrics?.members_growth || 0}
            trend={overview?.growth_metrics?.members_growth && overview.growth_metrics.members_growth > 0 ? 'up' : 'down'}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Pending Invites"
            value={overview?.pending_invitations || 0}
            subtitle="Awaiting responses"
            icon="email"
            color="users"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Shared Stories"
            value={overview?.shared_content || 0}
            subtitle={`${overview?.engagement_score || 0}% engagement`}
            icon="share"
            color="users"
            change={overview?.growth_metrics?.content_growth || 0}
            trend={overview?.growth_metrics?.content_growth && overview.growth_metrics.content_growth > 0 ? 'up' : 'down'}
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          aria-label="family dashboard tabs"
          TabIndicatorProps={{
            style: { backgroundColor: colors.users.main, height: 3 }
          }}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            label="Overview"
            icon={<AssessmentIcon />}
            iconPosition="start"
            sx={{ '&.Mui-selected': { color: colors.users.main } }}
          />
          <Tab
            label={
              <Badge badgeContent={moderationOverview?.pending_review || 0} color="warning">
                Moderation
              </Badge>
            }
            icon={<SecurityIcon />}
            iconPosition="start"
            sx={{ '&.Mui-selected': { color: colors.users.main } }}
          />
          <Tab
            label="Analytics"
            icon={<TrendingUpIcon />}
            iconPosition="start"
            sx={{ '&.Mui-selected': { color: colors.users.main } }}
          />
        </Tabs>

        {/* Overview Tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            {/* Family Engagement Chart */}
            <Grid item xs={12} lg={8}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Family Engagement Trends
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant={chartPeriod === 'weekly' ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => handleChartPeriodChange('weekly')}
                    >
                      Weekly
                    </Button>
                    <Button
                      variant={chartPeriod === 'monthly' ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => handleChartPeriodChange('monthly')}
                    >
                      Monthly
                    </Button>
                  </Box>
                </Box>
                <Box sx={{ height: 300 }}>
                  {engagementData && engagementData.labels && engagementData.datasets ? (
                    <Line 
                      data={engagementData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            display: false
                          }
                        },
                        scales: {
                          y: {
                            beginAtZero: true
                          }
                        }
                      }}
                    />
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                      <Typography variant="body2" color="text.secondary">
                        Loading chart data...
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>

            {/* Recent Activity */}
            <Grid item xs={12} lg={4}>
              <Paper sx={{ p: 3, height: 'fit-content' }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Recent Activity
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <List>
                  {recentActivities.slice(0, 5).map((activity, index) => (
                    <ListItem
                      key={activity.id}
                      sx={{
                        borderBottom: index < recentActivities.length - 1 ? 1 : 0,
                        borderColor: 'divider',
                        px: 0,
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            backgroundColor: alpha(getActivityColor(activity.activity_type, activity.severity), 0.1),
                            color: getActivityColor(activity.activity_type, activity.severity),
                          }}
                        >
                          {getActivityIcon(activity.activity_type)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle2">
                            {activity.family_name}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {activity.actor_name} {activity.description}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>

            {/* Top Active Families */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Top Active Families
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Family Name</TableCell>
                        <TableCell align="right">Members</TableCell>
                        <TableCell align="right">Stories</TableCell>
                        <TableCell align="right">Activity Score</TableCell>
                        <TableCell align="right">Last Activity</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {topFamilies.map((family) => (
                        <TableRow key={family.id}>
                          <TableCell>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {family.family_name}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{family.members_count}</TableCell>
                          <TableCell align="right">{family.stories_count}</TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                              <Box sx={{ width: 100, mr: 1 }}>
                                <LinearProgress 
                                  variant="determinate" 
                                  value={family.activity_score} 
                                  sx={{ height: 8, borderRadius: 4 }}
                                />
                              </Box>
                              <Typography variant="body2" color="text.secondary">
                                {family.activity_score}%
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="text.secondary">
                              {formatDistanceToNow(new Date(family.last_activity), { addSuffix: true })}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Moderation Tab */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Content Moderation
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography>Flagged Content:</Typography>
                  <Chip 
                    label={moderationOverview?.flagged_content || 0} 
                    color={moderationOverview?.flagged_content ? "warning" : "default"}
                    icon={<FlagIcon />}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography>Pending Review:</Typography>
                  <Chip 
                    label={moderationOverview?.pending_review || 0} 
                    color={moderationOverview?.pending_review ? "error" : "default"}
                    icon={<WarningIcon />}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography>Auto-Approved:</Typography>
                  <Chip 
                    label={moderationOverview?.auto_approved || 0} 
                    color="success"
                    icon={<CheckIcon />}
                  />
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={8}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Recent Flags
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <List>
                  {moderationOverview?.recent_flags?.map((flag, index) => (
                    <ListItem key={flag.id}>
                      <ListItemText
                        primary={flag.content_title}
                        secondary={`${flag.reason} • Flagged by ${flag.flagged_by}`}
                      />
                      <ListItemSecondaryAction>
                        <Chip
                          size="small"
                          label={flag.severity}
                          color={flag.severity === 'high' ? 'error' : 'warning'}
                          variant="outlined"
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  )) || (
                    <ListItem>
                      <ListItemText
                        primary="No recent flags"
                        secondary="All content is operating smoothly"
                      />
                    </ListItem>
                  )}
                </List>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Analytics Tab - Weekly Digest Analytics */}
        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={3}>
            {/* Header with timeframe selector */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  📧 Weekly Digest Analytics
                </Typography>
                <TimeRangeFilter
                  value={digestTimeframe}
                  onChange={handleDigestTimeframeChange}
                  onRefresh={() => loadDigestAnalytics(digestTimeframe)}
                  refreshing={digestLoading}
                />
              </Box>
            </Grid>

            {digestLoading ? (
              <Grid item xs={12}>
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography>Loading digest analytics...</Typography>
                </Paper>
              </Grid>
            ) : digestAnalytics ? (
              <>
                {/* Preferences Overview */}
                <Grid item xs={12} md={3}>
                  <MetricCard
                    title="Users with Digests"
                    value={digestAnalytics.preferences_overview?.total_users_with_preferences || 0}
                    color="users"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <MetricCard
                    title="Daily Enabled"
                    value={digestAnalytics.preferences_overview?.daily_enabled_count || 0}
                    color="content"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <MetricCard
                    title="Weekly Enabled"
                    value={digestAnalytics.preferences_overview?.weekly_enabled_count || 0}
                    color="revenue"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <MetricCard
                    title="Monthly Enabled"
                    value={digestAnalytics.preferences_overview?.monthly_enabled_count || 0}
                    color="costs"
                  />
                </Grid>

                {/* Delivery Stats */}
                {digestAnalytics.engagement_stats && digestAnalytics.engagement_stats.length > 0 && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 3 }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        Delivery Metrics
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Digest Type</TableCell>
                              <TableCell align="right">Sent</TableCell>
                              <TableCell align="right">Delivered</TableCell>
                              <TableCell align="right">Failed</TableCell>
                              <TableCell align="right">Bounced</TableCell>
                              <TableCell align="right">Delivery Rate</TableCell>
                              <TableCell align="right">Avg Events</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {digestAnalytics.engagement_stats.map((stat: any) => (
                              <TableRow key={stat.digest_type}>
                                <TableCell>
                                  <Chip label={stat.digest_type} size="small" />
                                </TableCell>
                                <TableCell align="right">{stat.total_sent || 0}</TableCell>
                                <TableCell align="right">{stat.total_delivered || 0}</TableCell>
                                <TableCell align="right">{stat.total_failed || 0}</TableCell>
                                <TableCell align="right">{stat.total_bounced || 0}</TableCell>
                                <TableCell align="right">
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: parseFloat(stat.delivery_rate) > 95 ? 'success.main' : parseFloat(stat.delivery_rate) > 90 ? 'warning.main' : 'error.main',
                                      fontWeight: 600,
                                    }}
                                  >
                                    {stat.delivery_rate}%
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">{stat.avg_events_per_digest || 0}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  </Grid>
                )}

                {/* Top Digests by Activity */}
                {digestAnalytics.top_performing_digests && digestAnalytics.top_performing_digests.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3 }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        Top Digests by Activity
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <List>
                        {digestAnalytics.top_performing_digests.map((digest: any, index: number) => (
                          <ListItem
                            key={digest.history_id || index}
                            sx={{
                              borderBottom: index < digestAnalytics.top_performing_digests.length - 1 ? 1 : 0,
                              borderColor: 'divider',
                            }}
                          >
                            <ListItemAvatar>
                              <Avatar sx={{ bgcolor: 'success.main' }}>
                                {index + 1}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Chip label={digest.digest_type} size="small" />
                                  <Typography variant="body2" color="text.secondary">
                                    {format(new Date(digest.sent_at), 'MMM dd, yyyy')}
                                  </Typography>
                                </Box>
                              }
                              secondary={
                                <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                                  <Typography variant="caption">
                                    Events: {digest.event_count || 0}
                                  </Typography>
                                  <Typography variant="caption">
                                    Shares: {digest.total_shares || 0}
                                  </Typography>
                                  <Typography variant="caption">
                                    Views: {digest.total_views || 0}
                                  </Typography>
                                  <Typography variant="caption">
                                    Comments: {digest.total_comments || 0}
                                  </Typography>
                                </Box>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  </Grid>
                )}

                {/* Failure Reasons */}
                {digestAnalytics.failure_reasons && digestAnalytics.failure_reasons.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3 }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        Failure Reasons
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <List>
                        {digestAnalytics.failure_reasons.map((reason: any, index: number) => (
                          <ListItem
                            key={reason.error_code || index}
                            sx={{
                              borderBottom: index < digestAnalytics.failure_reasons.length - 1 ? 1 : 0,
                              borderColor: 'divider',
                            }}
                          >
                            <ListItemAvatar>
                              <Avatar sx={{ bgcolor: 'error.main' }}>
                                <WarningIcon />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={reason.error_code || 'Unknown Error'}
                              secondary={`${reason.failure_count || 0} failures`}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  </Grid>
                )}
              </>
            ) : (
              <Grid item xs={12}>
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <EmailIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    No Digest Analytics Available
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Weekly digest analytics data will appear here once digests are sent.
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default FamilyCollaborationDashboard;